import React, { FC, ReactNode } from 'react';
import { Box, Container, Link, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';

/** iOS の内蔵ブラウザで開かれる前提なので、ヒラギノを先頭に置いた系統フォント。 */
const FONT =
  '-apple-system, BlinkMacSystemFont, "Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", sans-serif';

const BG = '#fff4e8';
const TEXT = '#1f1b16';

const body = {
  fontFamily: FONT,
  fontSize: '1rem',
  lineHeight: 1.95,
  color: TEXT,
};

type DocumentProps = {
  title: string;
  lead?: ReactNode;
  enactedOn: string;
  /** 特商法表記のように、制定日ではなく更新日を出したいときに差し替える。 */
  enactedLabel?: string;
  children: ReactNode;
};

/** CHECK の規約類の外枠。ページの title は useAnalytics が付ける。 */
export const Document: FC<DocumentProps> = ({
  title,
  lead,
  enactedOn,
  enactedLabel = '制定',
  children,
}) => (
  <Box sx={{ minHeight: '100vh', bgcolor: BG }}>
    <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 }, px: { xs: 3, sm: 4 } }}>
      <Typography
        component="h1"
        sx={{ ...body, fontSize: { xs: '1.75rem', sm: '2rem' }, fontWeight: 700, mb: 3 }}
      >
        {title}
      </Typography>
      {lead ? <Typography sx={{ ...body, mb: 2 }}>{lead}</Typography> : null}
      {children}
      <Box sx={{ mt: 8 }}>
        <Typography sx={{ ...body, textAlign: 'right' }}>
          【{enactedOn}
          {enactedLabel}】
        </Typography>
        <Typography sx={{ ...body, textAlign: 'right' }}>チェック運営</Typography>
      </Box>
    </Container>
  </Box>
);

export const Article: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <Box component="section" sx={{ mt: 5 }}>
    <Typography
      component="h2"
      sx={{ ...body, fontSize: '1.2rem', fontWeight: 700, mb: 1 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

export const Heading: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography component="h3" sx={{ ...body, fontWeight: 700, mt: 3 }}>
    {children}
  </Typography>
);

export const P: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography sx={{ ...body, mt: 1 }}>{children}</Typography>
);

const list = {
  ...body,
  mt: 1,
  pl: 3,
  '& li': { mt: 1 },
  '& li::marker': { color: TEXT },
};

/** 第◯条のなかの 1. 2. 3.。 */
export const Ol: FC<{ items: ReactNode[] }> = ({ items }) => (
  <Typography component="ol" sx={list}>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </Typography>
);

export const Ul: FC<{ items: ReactNode[] }> = ({ items }) => (
  <Typography component="ul" sx={list}>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </Typography>
);

/** 特商法表記の項目表。見出しの幅をそろえたいので Table で組む。 */
export const Rows: FC<{ rows: { label: string; value: ReactNode }[] }> = ({ rows }) => (
  <Table sx={{ mt: 2 }}>
    <TableBody>
      {rows.map(({ label, value }) => (
        <TableRow key={label}>
          <TableCell
            component="th"
            scope="row"
            sx={{
              ...body,
              fontWeight: 700,
              verticalAlign: 'top',
              width: { xs: '8.5rem', sm: '13rem' },
              px: { xs: 0, sm: 1 },
              borderColor: 'rgba(31, 27, 22, 0.12)',
            }}
          >
            {label}
          </TableCell>
          <TableCell
            sx={{ ...body, px: { xs: 1, sm: 2 }, borderColor: 'rgba(31, 27, 22, 0.12)' }}
          >
            {value}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const CONTACT_URL = 'https://forms.gle/D3wupP9oTJZUwz1E9';

/**
 * theme の primary が黒なので、下線が無いと本文と見分けが付かない。App.css の
 * `a { text-decoration: none !important }` を上書きするので !important が要る。
 */
export const A: FC<{ href: string; children: ReactNode }> = ({ href, children }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      color: 'inherit',
      textDecoration: 'underline !important',
      textUnderlineOffset: '0.2em',
    }}
  >
    {children}
  </Link>
);
